import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, of, Subscription, switchMap, tap, throwError } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigProvider } from "../../../../config-provider.service";
import { NgIf, NgOptimizedImage } from "@angular/common";
import { MatError, MatFormField, MatHint, MatPrefix, MatSuffix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatButton, MatButtonModule } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { UtilsProvider } from "../../../providers/utils-provider.service";
import { CurrentUser } from "../../../providers/current-user";
import { LangsProvider } from "../../../providers/langs-provider";
import { catchError, filter, map } from "rxjs/operators";
import { MatProgressBar } from "@angular/material/progress-bar";
import { Organization } from "../../../models/organization";
import { CurrentUserRolesProvider } from "../../../../organizations/providers/current-user-roles-provider";
import { OrganizationsNavigationProvider } from "../../../../organizations/providers/navigation-provider";
import { OrganizationMembership } from "../../../models/organization-membership";
import { FullScreenLoaderComponent } from "../../full-screen-loader/full-screen-loader";
import { AppRoutingService } from "../../../providers/app-routing-service";
import { IsLoggedInService } from "../../../guards/is-logged-in";
import { IsAppReadyGuard } from "../../../guards/is-app-ready";

@Component({
  selector: 'auth-form',
  providers: [
  ],
  styleUrls: ['./auth-form.scss'],
  standalone: true,
  imports: [
    NgOptimizedImage,
    NgIf,
    MatFormField,
    ReactiveFormsModule,
    MatIcon,
    TranslateModule,
    MatInput,
    MatButton,
    MatButtonModule,
    MatCheckbox,
    MatHint,
    MatError,
    FormsModule,
    MatPrefix,
    MatProgressBar,
    MatSuffix,
    FullScreenLoaderComponent
  ],
  templateUrl: './auth-form.html'
})
export class AuthFormComponent implements AfterViewInit, OnDestroy {
  public FLOW_DEFAULT = 'default';
  public FLOW_LOGIN = 'login';
  public FLOW_REGISTER = 'register';
  public FLOW_UNAUTHORIZED = 'unauthorized';
  public FLOW_RESET_PASSWORD = 'resetPassword';
  public PASSWORD_MIN_LENGTH = 6;
  public TOKEN_CHECKED_VALID = 'valid';

  public SLIDE_TRANSITION_DURATION = 300; // ms

  @ViewChild('emailControl', {static: false}) public emailControl: ElementRef | undefined;
  public emailFormControl: FormControl = new FormControl('', [
    Validators.email
  ]);

  @ViewChild('passwordControl', {static: false}) public passwordControl: ElementRef | undefined;
  public passwordFormControl: FormControl = new FormControl('', [
    Validators.required,
    // doc : https://blog.thoughtram.io/angular/2016/03/14/custom-validators-in-angular-2.html#building-a-custom-validator
    (control: any) => !this.passwordWrongError ? null : {passwordWrong: {valid: false}},
    (control: any) => !this.accountDisabledError ? null : {accountDisabled: {valid: false}}
  ]);
  public passwordWrongError = false;
  public accountDisabledError = false;

  @ViewChild('eulaControl', {static: false}) public eulaControl: ElementRef | undefined;
  public eulaFormControl: FormControl = new FormControl('', [
    (control: any) => this.eulaAccepted ? null : {eulaNotAccepted: {valid: false}}
  ]);

  @ViewChild('resetPasswordControl', {static: false}) public resetPasswordControl: ElementRef | undefined;
  public resetPasswordFormControl: FormControl = new FormControl('', [
    Validators.required,
    Validators.minLength(this.PASSWORD_MIN_LENGTH)
  ]);

  @ViewChild('registerPasswordControl', {static: false}) public registerPasswordControl: ElementRef | undefined;
  public registerPasswordFormControl: FormControl = new FormControl('', [
    Validators.required,
    Validators.minLength(this.PASSWORD_MIN_LENGTH)
  ]);
  @ViewChild('registerFirstNameControl', {static: false}) public registerFirstNameControl: ElementRef | undefined;
  public registerFirstNameFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);
  @ViewChild('registerLastNameControl', {static: false}) public registerLastNameControl: ElementRef | undefined;
  public registerLastNameFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);

  @ViewChild('applicationMessageControl', {static: false}) public applicationMessageControl: ElementRef | undefined;
  public applicationMessageFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);

  public isLoading = true;
  public isNewUser = false;
  public isIHaveAnAccountText = true;
  public emailStepState = 'before';
  public passwordStepState = 'after';
  public unauthorizedStepState = 'after';
  public forgotPasswordState = 'after';
  public resetPasswordState = 'after';
  public registerStepState = 'after';

  public email: string | null = null
  public password: string | null = null
  public rememberMe = true;
  public eulaAccepted = false;
  public resetPasswordToken: string | null = null
  public newPassword: string | null = null
  public registerPassword = '';
  public registerFirstName = '';
  public registerLastName = '';
  public defLang: string | null = null

  isChecked = false;

  private subscriptions: Subscription[] = [];

  @Input() public title = '';
  @Input() public image: string = this.conf.assetsUrl + 'img/app-logo_black.png';
  @Input() public nextUrl?: string;
  @Input() public defaultNextUrl?: string;
  @Input() public isRegistrationDisabled = false;
  constructor(
    public conf: ConfigProvider,
    public utils: UtilsProvider,
    public currentUser: CurrentUser,
    private _langs: LangsProvider,
    private _router: Router,
    private _route: ActivatedRoute,
    private translater: TranslateService,
    private _toaster: MatSnackBar,
    private _changeDetector: ChangeDetectorRef,
    private _currentUserRoles: CurrentUserRolesProvider,
    private _organizationNavigation: OrganizationsNavigationProvider,
  ) {
    this._route.queryParamMap.subscribe((params) => {
      if (params.has('email-reset-token')) {
        const token = params.get('email-reset-token');
        if (!token) {
          return;
        }
        this.currentUser.logout().subscribe(() => {
          this.currentUser.validateNewEmailToken(token).subscribe({
            next: () => {
              this._toaster.open(this.translater.instant('New_email_validated'), '', {duration: 5000});
            },
            error: (error) => this.onError(error)
          });
        });
      }
    });
  }

  public ngAfterViewInit() {
    this.retrieveParams();
    if (this.currentUser.isLoggedIn()) {
      this.startLoggedFlow();
      this.isLoading = false;
      this._changeDetector.detectChanges();
    } else {
      this.isLoading = false;
      if (this.defLang === null || this.defLang === '') {
        this.defLang = this._langs.getBestLangCode();
      }
      this._langs.selectLang(this.conf.defaultLang);
      if (this.resetPasswordToken) {
        this.selectFlow(this.FLOW_RESET_PASSWORD);
      } else {
        this.selectFlow(this.FLOW_DEFAULT);
      }
      this._changeDetector.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public onCheckEmailClicked(event: any): void {

    this.email = this.emailFormControl.value;
    // on blur systématiquement tous les events issu d'un textbox
    // car si l'utilisateur déclenche cet event via la toucher enter
    // le textbox reste focus même s'il est plus affiché
    // et l'utilisateur risque de taper au mauvais endroit ...
    event.target.blur();
    if (this.isLoading || this.emailFormControl.hasError('required') || this.emailFormControl.hasError('email')) {
      return;
    }

    this.isLoading = true;
    this.checkEmail().subscribe({
      next: (result: {flow:string, eula_accepted_at?:string|null}) => {
        this.selectFlow(result.flow);
        this.isLoading = false;
        if (result.eula_accepted_at) {
          this.eulaAccepted = true;
        }
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  public onEulaChanged(event: any): void {
    this.eulaFormControl.updateValueAndValidity();
  }

  public onCheckPasswordClicked(event: any): void {
    // on blur systématiquement tous les events issu d'un textbox
    // car si l'utilisateur déclenche cet event via la toucher enter
    // le textbox reste focus même s'il est plus affiché
    // et l'utilisateur risque de taper au mauvais endroit ...
    event.target.blur();

    if (this.isLoading
      || this.passwordFormControl.hasError('required')
      || this.passwordFormControl.hasError('passwordWrong')
      || this.passwordFormControl.hasError('accountDisabled')) {
      return;
    }
    if (this.isLoading
      || this.passwordFormControl.hasError('required')
      || this.passwordFormControl.hasError('passwordWrong')
      || this.passwordFormControl.hasError('accountDisabled')
      || !this.eulaAccepted) {
      return;
    }

    this.isLoading = true;
    this.checkPassword().subscribe({
      next: (result: any) => {
        this._langs.selectLang(this._langs.getBestLangConfig());
        this.startLoggedFlow();
        this.isLoading = false;
        this._changeDetector.detectChanges();
      },
      error: (error) => {
        if (error && error.code) {
          this.isLoading = false;
          switch (error.code) {
            case 1:
              this.passwordWrongError = true;
              break;
            case 3:
              this.accountDisabledError = true;
              break;
            case 2:
            default:
              this.onError(error);
              break;
          }
          this.passwordFormControl.updateValueAndValidity();
        } else {
          this.onError(error);
        }
      }
    });
  }

  public onPasswordKeyPressed(): void {
    this.passwordWrongError = false;
    this.accountDisabledError = false;
  }

  public onNotYouClicked(): void {
    this.password = '';
    this.selectFlow(this.FLOW_DEFAULT);
  }

  public onForgotPasswordClicked(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.sendPasswordResetEmail().subscribe({
      next: (result: any) => {
        this.passwordStepState = 'before';
        this.forgotPasswordState = 'active';
        this.isLoading = false;
      },
      error: (error) => this.onError(error)
    });
  }

  public onResetPasswordClicked(event: any): void {
    // on blur systématiquement tous les events issu d'un textbox
    // car si l'utilisateur déclenche cet event via la toucher enter
    // le textbox reste focus même s'il est plus affiché
    // et l'utilisateur risque de taper au mauvais endroit ...
    event.target.blur();

    if (this.isLoading || this.resetPasswordFormControl.hasError('required')) {
      return;
    }

        this.isLoading = true;
        this.resetPassword().subscribe({
            next: (result: any) => {
                this.rememberMe = false;
                this.password = this.newPassword;
                this.checkPassword().subscribe({
                    next: (result: any) => {
                        this._langs.selectLang(this._langs.getBestLangConfig());
                        this.isLoading = false;
                        this._changeDetector.detectChanges();
                        // detectChanges() is not enough to trigger changes from currentUser
                        window.location.href = '';
                    },
                    error: (error) => this.onError(error)
                });
            },
            error: (error) => this.onError(error)
        });
    }

  public onCheckRegisterClicked(event: any): void {
    // on blur systématiquement tous les events issu d'un textbox
    // car si l'utilisateur déclenche cet event via la toucher enter
    // le textbox reste focus même s'il est plus affiché
    // et l'utilisateur risque de taper au mauvais endroit ...
    event.target.blur();

    if (this.isLoading
      || this.registerPasswordFormControl.hasError('required')
      || this.registerPasswordFormControl.hasError('minlength')
      || this.registerFirstNameFormControl.hasError('required')
      || this.registerLastNameFormControl.hasError('required')) {
      return;
    }

    this.isLoading = true;
    this.register().subscribe({
      next: (result: any) => {
        this.startLoggedFlow();
        this.isLoading = false;
        this._changeDetector.detectChanges();
      },
      error: (error) => this.onError(error)
    })
  }

  public onUseAccountClicked(): void {
    this.isIHaveAnAccountText = true;
  }

  public onNoAccountClicked(): void {
    this.isIHaveAnAccountText = false;
  }

  public onCompleteProfileClicked(): void {
    this._router.navigate(['users', this.currentUser.user?.id, 'edit']);
  }

  public onBackHomeClicked(): void {
    // pour une sombre raison, le routing standard change l'url dans le navigateur
    // mais n'initie pas de rechargement de la page en fonction de l'url ...
    // du coup recharge tout de façon abrupte ...
    window.location.href = '';
  }

  private retrieveParams(): void {
    this.subscriptions.push(
      this._route.queryParamMap.subscribe((params) => {
        if (params.has('authEmail') && params.has('authToken')) {
          this.email = params.get('authEmail');
          this.resetPasswordToken = params.get('authToken');
          this.defLang = params.get('defLang');
        } else if (params.has('nextUrl')) {
          this.nextUrl = params.get('nextUrl') ?? undefined;
        }
        // pour éviter l'erreur décrite ici : https://github.com/angular/angular/issues/6005
        // on utilise ce workaround : https://stackoverflow.com/questions/34827334/triggering-angular2-change-detection-manually
        // TODO : Is that nécessaire ?
        this._changeDetector.detectChanges();
      })
    );
  }

  private onError(error: any): void {
    console.error(error);
    this.isLoading = false;
    this._toaster.open(this.translater.instant('Error_unknown'), '', {duration: 5000});
  }

  private checkEmail(): Observable<{flow:string, eula_accepted_at?: string|null}> {
    if (!this.email) {
      return throwError(() => this.onError({error: 'Email not set'}));
    }
    return this.currentUser.checkEmail(this.email).pipe(
      map((result: any) => {
        return result;
      }),
      catchError((error) => {
        return throwError(() => this._toaster.open(this.translater.instant('Email_invalid'), '', {duration: 5000}));
      })
    );
  }

  private checkPassword(): Observable<any> {
    if (!this.email || !this.password) {
      return throwError(() => this.onError({error: 'Email or password not set'}));
    }
    if (!this.eulaAccepted) {
      if (this.resetPasswordState === 'active') {
        this.selectFlow(this.FLOW_LOGIN);
      } else {
        return throwError(() => this.onError({error: 'EULA not accepted'}));
      }
    }
    return this.currentUser.login(this.email, this.password, this.rememberMe, this.eulaAccepted).pipe(
      map((result: any) => {
        return result;
      }),
    );
  }

  private sendPasswordResetEmail(): Observable<any> {
    const resetPasswordUrl: string = this.conf.apiRootUrl + 'auth';
    // REPLACEMENT OF THE FOLLOWING CODE WHILE UPGRADE
    return this.currentUser.sendPasswordResetEmail(this.email!, resetPasswordUrl);
  }

  private resetPassword(): Observable<any> {
    // REPLACEMENT OF THE FOLLOWING CODE WHILE UPGRADE
    return this.currentUser.resetPassword(this.email!, this.newPassword!, this.resetPasswordToken!);
  }

  private register(): Observable<any> {
    return this.currentUser.register(
      this.email!,
      this.registerPassword,
      this.rememberMe,
      this._langs.getBestLangId(),
      this.registerFirstName,
      this.registerLastName,
      this.eulaAccepted
    ).pipe(
      map((result: any) => {
        // this._sync.sync().then(() => resolve()).catch((error) => reject(error));
        return result;
      }),
      catchError((error) => {
        return throwError(() => this.onError(error));
      })
    );
  }

  private startLoggedFlow(): void {
    return this.redirectUser();
  }

  private selectFlow(flow: string): void {
    if (flow === this.FLOW_DEFAULT) {
      this.emailStepState = 'active';
      this.passwordStepState = 'after';
      this.forgotPasswordState = 'after';
      this.unauthorizedStepState = 'after';
      this.resetPasswordState = 'after';
      this.registerStepState = 'after';
      setTimeout(() => this.emailControl?.nativeElement.focus(), this.SLIDE_TRANSITION_DURATION);
    } else if (flow === this.FLOW_LOGIN) {
      this.emailStepState = 'before';
      this.passwordStepState = 'active';
      this.forgotPasswordState = 'after';
      this.unauthorizedStepState = 'after';
      this.resetPasswordState = 'after';
      this.registerStepState = 'after';
      setTimeout(() => this.passwordControl?.nativeElement.focus(), this.SLIDE_TRANSITION_DURATION);
    } else if (flow === this.FLOW_RESET_PASSWORD) {
      this.checkResetToken().subscribe({
        next: (result: any) => {
          if (result === this.TOKEN_CHECKED_VALID) {
            this.emailStepState = 'before';
            this.resetPasswordState = 'active';
            setTimeout(() => this.resetPasswordControl?.nativeElement.focus(), this.SLIDE_TRANSITION_DURATION);
          } else {
            this.emailStepState = 'active';
            setTimeout(() => this.emailControl?.nativeElement.focus(), this.SLIDE_TRANSITION_DURATION);
            this._toaster.open(this.translater.instant('Reset_password_token_invalid'), '', {duration: 5000});
          }
        },
        error: (error) => this.onError(error)
      });
    } else if (flow === this.FLOW_UNAUTHORIZED) {
      this.emailStepState = 'before';
      this.passwordStepState = 'before';
      this.unauthorizedStepState = 'active';
    } else if (flow === this.FLOW_REGISTER) {
      this.isNewUser = true;
      this.emailStepState = 'before';
      this.registerStepState = 'active';
      setTimeout(() => this.registerFirstNameControl?.nativeElement.focus(), this.SLIDE_TRANSITION_DURATION);
    } else {
      this.onError({error: 'Unknown auth flow[' + flow + ']'});
    }
  }

  private checkResetToken(): Observable<string> {
    return this.currentUser.checkResetToken(this.email!, this.resetPasswordToken!).pipe(
      map((result: any) => {
        return result.checked;
      }),
      catchError((error) => {
        return throwError(() => this.onError(error));
      })
    );
  }

  private redirectUser(): void {
    // Si une URL de redirection est déjà définie, rediriger immédiatement.
    if (this.nextUrl) {
      this._router.navigateByUrl(this.nextUrl);
      return;
    }

    if (this.defaultNextUrl) {
      this._router.navigateByUrl(this.defaultNextUrl);
      return;
    }

    this.currentUser.getOrganizationsMemberships().pipe(
      switchMap((organizationMemberships: OrganizationMembership[]) => {
        if (organizationMemberships.length === 1) {
          const organization = organizationMemberships[0].organization!;
          const roles = organizationMemberships[0].organizationRoles.map(role => role.id!);
          return this.currentUser.getMainHomeUrl(organization, roles);
        } else {
          // S'il n'y a pas exactement une organisation, on essaie de récupérer l'organisation depuis l'URL
          return this._organizationNavigation.getOrganisationFromRoute().pipe(
            switchMap((organization: Organization | null) => {
              if (organization) {
                const roles = this._currentUserRoles.getRolesIds(organization.id!);
                return this.currentUser.getMainHomeUrl(organization, roles);
              }
              return of('/organizations');  // Chemin par défaut si aucune organisation n'est trouvée
            })
          );
        }
      }),
      catchError((error) => {
        this.onError(error);
        return of('/error');  // Chemin d'erreur par défaut
      }),
      tap((url: string) => {
        this._router.navigateByUrl(url);
      })
    ).subscribe();
  }


  public toggleFieldType() {
    this.isChecked =! this.isChecked;
  }

}
